import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { practiceAPI } from '../../api';

const ImportWizard = ({ isOpen, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('paste');
  const [title, setTitle] = useState('');
  const [csvText, setCsvText] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const exampleCSV = `question,option_1,option_2,option_3,option_4,answer
What is the capital of France?,Berlin,Paris,London,Madrid,2
What is 2+2?,3,4,5,6,2`;

  const csvCommand = `Generate 20 multiple-choice questions about the French Revolution (causes, key events, outcomes). Vary difficulty: 8 easy, 8 medium, 4 hard. Output CSV with columns: question, option_1, option_2, option_3, option_4, answer. Use letters A-D for the answer. Ensure options are plausible and not obvious.`;

  const wordCommand = `1. A computer is an electronic device that takes ______, processes it, and gives output.
A) Result
B) Data
C) Memory
D) Electricity
Answer: B

2. The main task of the computer can be summarized as ______.
A) Copy–Paste–Delete
B) Input–Process–Output
C) Write–Read–Print
D) Save–Load–Store
Answer: B

3. Which of these is not a characteristic of a computer?
A) Speed
B) Diligence
C) Tiredness
D) Accuracy
Answer: C`;

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`);
  };

  const handlePasteSubmit = async () => {
    if (!title.trim()) return toast.error('Please enter a title');
    if (!csvText.trim()) return toast.error('Please paste CSV content');
    setLoading(true);
    try {
      await practiceAPI.importFromText(title, csvText);
      onSuccess();
      onClose();
      setTitle('');
      setCsvText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSubmit = async () => {
    if (!title.trim()) return toast.error('Please enter a title');
    if (!file) return toast.error('Please select a file');
    const formData = new FormData();
    formData.append('title', title);
    formData.append('file', file);
    setLoading(true);
    try {
      await practiceAPI.importFromFile(title, file);
      onSuccess();
      onClose();
      setTitle('');
      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity duration-300 ease-in-out" 
          aria-hidden="true"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75 backdrop-blur-sm"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full animate-scale-in">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="text-lg font-bold text-white">
                  Import Questions
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-blue-200 transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="bg-white px-6 pt-6 pb-4 max-h-[70vh] overflow-y-auto">
            {/* Title Input */}
            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                Set Title <span className="text-red-500">*</span>
              </label>
              <div className="relative group">
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={loading}
                  className="w-full px-4 py-3 pl-11 border-2 border-gray-200 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 group-hover:border-blue-300"
                  placeholder="e.g., Math Practice, Biology Quiz"
                  required
                  autoFocus
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('paste')}
                  className={`py-2 px-4 font-medium transition-all duration-200 ${
                    activeTab === 'paste' 
                      ? 'text-blue-600 border-b-2 border-blue-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Paste CSV
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('upload')}
                  className={`py-2 px-4 font-medium transition-all duration-200 ${
                    activeTab === 'upload' 
                      ? 'text-blue-600 border-b-2 border-blue-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Upload File
                </button>
              </nav>
            </div>

            {activeTab === 'paste' && (
              <div>
                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mb-4">
                  <div className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">CSV Format Required</p>
                      <p className="text-xs text-blue-600 mt-1">
                        First row must have headers: <code className="text-xs">question, option_1, option_2, option_3, option_4, answer</code>
                        <br />
                        Answer can be index (1‑based) or letter (A,B,C,D).
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Command for CSV */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <p className="text-sm font-semibold text-purple-800 flex items-center">
                      <span className="text-lg mr-2">🤖</span>
                      Try this with ChatGPT / Claude / Gemini:
                    </p>
                    <button
                      onClick={() => copyToClipboard(csvCommand, 'CSV command')}
                      className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition flex items-center gap-1 self-start sm:self-auto"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy Command
                    </button>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 overflow-x-auto">
                    <code className="text-xs text-green-400 whitespace-pre-wrap break-words">
                      {csvCommand}
                    </code>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    📌 Paste this command into any AI chat, replace the topic and question count, then copy the CSV output below!
                  </p>
                </div>

                {/* Textarea for CSV */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Paste CSV Content
                  </label>
                  <textarea
                    rows={6}
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={exampleCSV}
                  />
                </div>

                <div className="text-xs text-gray-500">
                  Tip: AI can generate this CSV for you – just copy and paste.{" "}
                  <a
                    href="https://cbthost.com/ai-quiz-generator-from-pdf-word-csv-text-instant-question-import-tool"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Learn how to use our AI Question Generator →
                  </a>
                </div>
              </div>
            )}

            {activeTab === 'upload' && (
              <div>
                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mb-4">
                  <div className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Supported Formats</p>
                      <p className="text-xs text-blue-600 mt-1">
                        <strong>.xlsx, .xls, .csv, .docx, .pdf</strong>
                        <br />
                        📄 For Word/PDF: Use the numbered list format shown below
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Command for Word/Text Format */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                    <p className="text-sm font-semibold text-emerald-800 flex items-center">
                      <span className="text-lg mr-2">📝</span>
                      Tell AI to format questions like this:
                    </p>
                    <button
                      onClick={() => copyToClipboard(wordCommand, 'Word format template')}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition flex items-center gap-1 self-start sm:self-auto"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      Copy Template
                    </button>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <code className="text-xs text-green-400 whitespace-pre-wrap break-words">
                      {wordCommand}
                    </code>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Tell AI: "Generate questions in this exact numbered format with Answer: X at the end of each question"
                  </p>
                </div>

                {/* File Upload */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select File
                  </label>
                  <label className="block w-full p-6 border-2 border-dashed border-gray-300 rounded-xl text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-200">
                    <input 
                      type="file" 
                      accept=".xlsx,.xls,.csv,.docx,.pdf" 
                      className="hidden" 
                      onChange={(e) => setFile(e.target.files[0])} 
                    />
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm text-gray-600 break-all">
                        {file ? file.name : 'Click or drag to upload (CSV, Excel, Word, PDF)'}
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all duration-200 w-full sm:w-auto order-2 sm:order-1"
            >
              Cancel
            </button>
            <button
              onClick={activeTab === 'paste' ? handlePasteSubmit : handleFileSubmit}
              disabled={loading || (activeTab === 'paste' ? !csvText.trim() : !file) || !title.trim()}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none w-full sm:w-auto order-1 sm:order-2"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-4 w-4 text-white mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Importing...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span>Import Questions</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9) translateY(-10px);
          }
          to { 
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ImportWizard;